import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { useCurrentRouteKey } from '#app/hooks/useCurrentRouteKey.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { PropertyButtonName } from '#app/types/analytics/propertyLibrary.js';
import { getIndependentPortfolioUrl } from '#app/utilities/getIndependentPortfolioUrl.js';
import { prettifyRouteKey } from '#app/utilities/prettifyRouteKey.js';
import { getRoute, getUrl } from '@contra/routes';
import { useCallback } from 'react';

type GetPortfolioLinkPropsArgs = {
  path?: string;
  urlParameters?: { [key: string]: boolean | string } & {
    utm_campaign: IndependentPortfolioLinkSectionNames | string;
  };
};

type GetPortfolioLinkPropsReturn = {
  href: string | undefined;
  onClick: (() => void) | undefined;
};

type GetUserPortfolioLinkPropertyArgs = {
  urlParameters:
    | null
    | ({ [key: string]: boolean | string } & {
        utm_campaign: IndependentPortfolioLinkSectionNames | string;
      });
  username: string;
};

type HandleCopyPortfolioLinkArgs = {
  buttonName: PropertyButtonName;
  section?: string;
  utmCampaign?: string;
};

type IndependentPortfolioLinkSectionNames =
  | 'independent_portfolio_nav_header'
  | 'independent_portfolio_palette_icon'
  | 'independent_portfolio_redirect'
  | 'independent_portfolio_settings_cta'
  | 'independent_portfolio_toast_link';

type UseCopyPortfolioLinkArgs = {
  hasPublishedPortfolio: boolean;
};

type UseCopyPortfolioLinkReturn = {
  handleCopyPortfolioLink: (args: HandleCopyPortfolioLinkArgs) => void;
};

type UsePortfolioLinkReturn = {
  getPortfolioLinkProps: (
    args: GetPortfolioLinkPropsArgs,
  ) => GetPortfolioLinkPropsReturn;
  getPortfolioUrl: (args: GetPortfolioLinkPropsArgs) => string | undefined;
};

const DEFAULT_PARAMETERS = {
  isInternalTraffic: true,
  utm_medium: 'contra',
};

const stringifyParameters = (parameters: Record<string, boolean | string>) => {
  return Object.fromEntries(
    Object.entries(parameters).map(([key, value]) => [key, String(value)]),
  );
};

export const useUserPortfolioLink = () => {
  const { track } = useAnalytics();
  const currentUser = useCurrentUser();
  const getPortfolioProjectUrl = useCallback(
    ({
      portfolioProjectSlug,
      urlParameters,
      username,
    }: GetUserPortfolioLinkPropertyArgs & { portfolioProjectSlug: string }) => {
      const parameters = new URLSearchParams(
        stringifyParameters({
          ...DEFAULT_PARAMETERS,
          ...urlParameters,
        }),
      );

      const portfolioUrl = getIndependentPortfolioUrl({
        username,
      });

      return currentUser
        ? getRoute('/auth/custom-domain', {
            search: {
              ...DEFAULT_PARAMETERS,
              ...urlParameters,
              username,
            },
          })
        : `${portfolioUrl}/p/${portfolioProjectSlug}?${parameters.toString()}`;
    },
    [currentUser],
  );

  const getPortfolioUrl = useCallback(
    ({ urlParameters, username }: GetUserPortfolioLinkPropertyArgs) => {
      const parameters = new URLSearchParams(
        stringifyParameters({
          ...DEFAULT_PARAMETERS,
          ...urlParameters,
        }),
      );

      const basePortfolioUrl = getIndependentPortfolioUrl({
        username,
      });

      return currentUser
        ? getRoute('/auth/custom-domain', {
            search: {
              ...DEFAULT_PARAMETERS,
              ...urlParameters,
              username,
            },
          })
        : `${basePortfolioUrl}?${parameters.toString()}`;
    },
    [currentUser],
  );

  const getPortfolioLinkProps = useCallback(
    ({ urlParameters, username }: GetUserPortfolioLinkPropertyArgs) => {
      const url = getPortfolioUrl({ urlParameters, username });

      return {
        href: url,
        onClick: () => {
          track('link', {
            action: 'tapped',
            destination_url: url,
            link_name: 'view_independent_portfolio',
            section: urlParameters?.utm_campaign,
          });
        },
      };
    },
    [getPortfolioUrl, track],
  );

  const getPortfolioProjectLinkProps = useCallback(
    ({
      portfolioProjectSlug,
      urlParameters,
      username,
    }: GetUserPortfolioLinkPropertyArgs & { portfolioProjectSlug: string }) => {
      const url = getPortfolioProjectUrl({
        portfolioProjectSlug,
        urlParameters,
        username,
      });

      return {
        href: url,
        onClick: () => {
          track('link', {
            action: 'tapped',
            destination_url: url,
            link_name: 'view_independent_portfolio',
            section: urlParameters?.utm_campaign,
          });
        },
      };
    },
    [getPortfolioProjectUrl, track],
  );

  return {
    getPortfolioLinkProps,
    getPortfolioProjectLinkProps,
    getPortfolioProjectUrl,
    getPortfolioUrl,
  };
};

export const useCurrentUserPortfolioLink = (): UsePortfolioLinkReturn => {
  const currentRouteKey = useCurrentRouteKey();
  const currentUser = useCurrentUser();
  const { track } = useAnalytics();

  const getPortfolioUrl = useCallback(
    ({ urlParameters }: GetPortfolioLinkPropsArgs) => {
      const parameters = {
        ...DEFAULT_PARAMETERS,
        utm_source: prettifyRouteKey(currentRouteKey),
        ...urlParameters,
      };

      const routeWithParameters = getRoute('/auth/custom-domain', {
        search: {
          ...parameters,
        },
      });
      return currentUser ? routeWithParameters : undefined;
    },
    [currentRouteKey, currentUser],
  );

  const getPortfolioLinkProps = useCallback(
    ({ path, urlParameters }: GetPortfolioLinkPropsArgs) => {
      const url = getPortfolioUrl({ path, urlParameters });

      return {
        href: url,
        onClick: () => {
          track('link', {
            action: 'tapped',
            destination_url: url,
            link_name: 'view_independent_portfolio',
            section: urlParameters?.utm_campaign,
          });
        },
      };
    },
    [getPortfolioUrl, track],
  );

  return { getPortfolioLinkProps, getPortfolioUrl };
};

export const useCopyCurrentUserPortfolioLink = ({
  hasPublishedPortfolio,
}: UseCopyPortfolioLinkArgs): UseCopyPortfolioLinkReturn => {
  const currentRouteKey = useCurrentRouteKey();
  const currentUser = useCurrentUser();
  const { track } = useAnalytics();

  const username = currentUser?.profile?.displayUsername ?? '';

  const getShareablePortfolioUrl = useCallback(
    ({ urlParameters }: GetPortfolioLinkPropsArgs) => {
      if (!username || !hasPublishedPortfolio) {
        return null;
      }

      const parameters = new URLSearchParams(
        stringifyParameters({
          ...DEFAULT_PARAMETERS,
          ...urlParameters,
          utm_source: prettifyRouteKey(currentRouteKey),
        }),
      );

      const basePortfolioUrl = getIndependentPortfolioUrl({
        username,
      });

      return `${basePortfolioUrl}?${parameters.toString()}`;
    },
    [currentRouteKey, hasPublishedPortfolio, username],
  );

  const copyToClipboard = useCopyToClipboard();

  const handleCopyPortfolioLink = useCallback(
    (analyticsDetails: HandleCopyPortfolioLinkArgs) => {
      const portfolioUrl =
        getShareablePortfolioUrl({
          urlParameters: analyticsDetails?.utmCampaign
            ? { utm_campaign: analyticsDetails.utmCampaign }
            : undefined,
        }) ??
        getUrl('/@username', {
          args: { username: username ?? 'me' },
        });

      track('button', {
        action: 'tapped',
        button_name: analyticsDetails.buttonName,
        section: analyticsDetails.section,
      });

      void copyToClipboard(portfolioUrl, {
        successToast: `Successfully copied ${
          username && hasPublishedPortfolio ? 'portfolio' : 'profile'
        } link to clipboard!`,
      });
    },
    [
      copyToClipboard,
      getShareablePortfolioUrl,
      hasPublishedPortfolio,
      track,
      username,
    ],
  );

  return { handleCopyPortfolioLink };
};
